// Minimal promise wrapper over the one IndexedDB database the app owns.
// IndexedDB, unlike localStorage, isn't capped at ~5 MB and doesn't throw
// the moment the quota is near, so unsent teacher work kept here survives a
// nearly full phone.
const DB_NAME = "cpd-offline";
const DB_VERSION = 2;
export const OUTBOX_STORE = "outbox";
export const REFS_STORE = "refs";
export const EVENTS_STORE = "events";

let opening: Promise<IDBDatabase> | null = null;

export function openDB(): Promise<IDBDatabase> {
  if (!opening) {
    opening = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        // Auto-incremented keys give replay order for free.
        if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
          db.createObjectStore(OUTBOX_STORE, { keyPath: "seq", autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(REFS_STORE)) db.createObjectStore(REFS_STORE);
        if (!db.objectStoreNames.contains(EVENTS_STORE)) {
          db.createObjectStore(EVENTS_STORE, { keyPath: "event_uuid" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        opening = null;
        reject(request.error);
      };
    });
  }
  return opening;
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAll<T>(store: string): Promise<T[]> {
  const db = await openDB();
  return done(db.transaction(store).objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function getAllEntries<T>(store: string): Promise<[string, T][]> {
  const db = await openDB();
  const tx = db.transaction(store).objectStore(store);
  const [keys, values] = await Promise.all([done(tx.getAllKeys()), done(tx.getAll())]);
  return keys.map((key, index) => [String(key), values[index] as T]);
}

export async function add<T>(store: string, value: T): Promise<IDBValidKey> {
  const db = await openDB();
  return done(db.transaction(store, "readwrite").objectStore(store).add(value));
}

export async function put<T>(store: string, value: T, key?: IDBValidKey): Promise<void> {
  const db = await openDB();
  await done(db.transaction(store, "readwrite").objectStore(store).put(value, key));
}

export async function remove(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDB();
  await done(db.transaction(store, "readwrite").objectStore(store).delete(key));
}

export async function clear(store: string): Promise<void> {
  const db = await openDB();
  await done(db.transaction(store, "readwrite").objectStore(store).clear());
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function putMany<T>(store: string, values: T[]): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(store, "readwrite");
  values.forEach((value) => tx.objectStore(store).put(value));
  await finished(tx);
}

export async function removeMany(store: string, keys: IDBValidKey[]): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(store, "readwrite");
  keys.forEach((key) => tx.objectStore(store).delete(key));
  await finished(tx);
}
